import { MemberPortal } from '@/@types';
import { eventAnalytics } from '@/providers';
import { TRACK_EVENTS } from '@/providers/eventAnalytics/trackEvents';
import { cn, TextLayout } from '@lib-atria/ui-toolkit';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ScorecardSummaryBar } from './scorecardSummaryBar';
import { ScoreCardSummarySkeletonLoader } from './scorecardSummarySkeleton';

type props = {
  analytes: MemberPortal.FindAllBiomarkers.AllAnalytesTableList | null;
};

export function ScorecardSummary({ analytes: allAnalytesCards }: props) {
  const BASE_BAR_HEIGHT = 372;
  const [biomarkersCounter, setBiomarkersCounter] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [outOfRangeCount, setOutOfRangeCount] = useState(0);
  const [normalCount, setNormalCount] = useState(0);
  const [outOfRangeHeight, setOutOfRangeHeight] = useState(BASE_BAR_HEIGHT);
  const [normalHeight, setNormalHeight] = useState(BASE_BAR_HEIGHT);
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [loading, setLoading] = useState(false);
  const isDesktopOrLargeTablet = useMediaQuery({ query: '(min-width: 1024px)' });

  const groupsNames = useMemo(() => {
    if (!allAnalytesCards?.length) return [];

    const filteredTitles = allAnalytesCards
      .map(({ title }) => title)
      .filter((title) => title !== 'Other')
      .sort();
    const hasOther = allAnalytesCards.some(({ title }) => title === 'Other');

    return ['All', ...filteredTitles, ...(hasOther ? ['Other'] : [])];
  }, [allAnalytesCards]);

  useEffect(() => {
    setLoading(true);
    const filteredGroups = allAnalytesCards?.filter((group) => {
      if (selectedGroup === 'All') {
        return true;
      }

      return group.tag === selectedGroup;
    });
    if (!filteredGroups) return;

    const groupOverviews = filteredGroups
      .filter((item) => item.overview)
      .map(({ overview }) => overview!);
    let normalItems = 0;
    let outOfRangeItems = 0;
    if (selectedGroup === 'All') {
      const startedAt = groupOverviews.reduce(
        (previous, { startAt }) => {
          const date = DateTime.fromISO(startAt);
          if (previous === null || date.toMillis() < previous.toMillis()) {
            return date;
          }
          return previous;
        },
        null as DateTime | null
      );

      const endedAt = groupOverviews.reduce(
        (previous, { endAt }) => {
          const date = DateTime.fromISO(endAt);
          if (previous === null || date.toMillis() > previous.toMillis()) {
            return date;
          }
          return previous;
        },
        null as DateTime | null
      );
      normalItems = groupOverviews.reduce((sum, { normal }) => sum + normal, 0);
      outOfRangeItems = groupOverviews.reduce((sum, { outOfRange }) => sum + outOfRange, 0);

      setStartDate(startedAt?.toFormat('LLL yyyy') || '');
      setEndDate(endedAt?.toFormat('LLL yyyy') || '');
      setBiomarkersCounter(
        groupOverviews.map(({ count }) => count).reduce((acc, total) => acc + total, 0)
      );
    } else {
      const biomarkers = filteredGroups.reduce((sum, group) => {
        const groupItems = group.items || [];
        groupItems.forEach((item) => {
          if (['normal', 'optimal'].includes((item.status || '').toLowerCase())) {
            normalItems++;
          } else if (item.status !== null) {
            outOfRangeItems++;
          }
        });

        return sum + groupItems.length;
      }, 0);
      const groupStartAt = DateTime.fromISO(groupOverviews[0]?.startAt || '');
      const groupEndAt = DateTime.fromISO(groupOverviews[0]?.endAt || '');

      setStartDate(groupStartAt?.isValid ? groupStartAt.toFormat('LLL yyyy') : '');
      setEndDate(groupEndAt?.isValid ? groupEndAt.toFormat('LLL yyyy') : '');
      setBiomarkersCounter(biomarkers);
    }
    setNormalCount(normalItems!);
    setOutOfRangeCount(outOfRangeItems!);
    if (normalItems > outOfRangeItems) {
      setOutOfRangeHeight(BASE_BAR_HEIGHT * (outOfRangeItems / normalItems));
      setNormalHeight(BASE_BAR_HEIGHT);
    } else {
      setNormalHeight(BASE_BAR_HEIGHT * (normalItems / outOfRangeItems));
      setOutOfRangeHeight(BASE_BAR_HEIGHT);
    }

    setLoading(false);
  }, [allAnalytesCards, groupsNames, selectedGroup]);

  const onSelectGroup = useCallback(
    (groupName: string) => () => {
      eventAnalytics.track(TRACK_EVENTS.OVERVIEW_BIOMARKER_CATEGORY_NAME_CLICKED, {
        biomarkerCategory: groupName,
      });
      setSelectedGroup(groupName);
    },
    []
  );

  const scrollIntoView = useCallback(
    (barLabel?: string) => {
      const section = document.querySelector(`[data-tag="${selectedGroup}"]`);
      if (!section) return;
      eventAnalytics.track(TRACK_EVENTS.OVERVIEW_BIOMARKER_CATEGORY_RESULTS_BAR_CLICKED, {
        biomarkerCategory: selectedGroup,
        barLabel,
      });
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    [selectedGroup]
  );

  return (
    <div className='flex flex-col lg:flex-row gap-[1px]'>
      {isDesktopOrLargeTablet && (
        <div className='flex w-full flex-col justify-between bg-kelp text-white rounded-lg py-6 px-6'>
          <h2 className={cn(TextLayout.h2.scto)}>Overview</h2>
          <table className='w-full text-left'>
            <thead>
              <tr>
                <th className={cn(TextLayout.body2, 'text-sm py-[1px] w-[50%]')}></th>
                <th className={cn(TextLayout.body2, 'text-sm py-[1px] w-[50%]')}></th>
              </tr>
            </thead>
            <tbody className={cn(TextLayout.body2, 'text-sm')}>
              {groupsNames.map((groupName, index) => (
                <tr
                  key={index}
                  className={cn(
                    'text-white opacity-60 hover:opacity-100 cursor-pointer',
                    TextLayout.body2,
                    {
                      'opacity-100': groupName === selectedGroup,
                    }
                  )}
                  onClick={onSelectGroup(groupName)}
                >
                  <td className='py-[1px]'>
                    {index + 1 < 10 && '0'}
                    {index + 1}
                  </td>
                  <td className='py-[1px]'>{groupName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className='w-full bg-kelp text-white rounded-lg py-6 px-6'>
        {loading ? (
          <ScoreCardSummarySkeletonLoader />
        ) : (
          <>
            {!isDesktopOrLargeTablet && (
              <h2 className={cn(TextLayout.h2.scto, 'pb-10')}>Overview</h2>
            )}
            <h2 className={cn(TextLayout.callout3, 'text-base  text-white')}>
              {selectedGroup} biomarker{biomarkersCounter !== 1 ? 's' : ''}
            </h2>
            <p className={cn(TextLayout.body1, 'text-base  text-white opacity-55 mb-6')}>
              {startDate} - {endDate}
            </p>
            <p className={cn(TextLayout.body1, 'text-base  text-white opacity-55 mb-16')}>
              Don&apos;t be concerned if any biomarkers are out of range—your doctor will reach out
              if anything needs your attention.
            </p>
            {normalCount === 0 && outOfRangeCount === 0 ? (
              <div className='flex flex-row gap-1 text-kelp items-end justify-center bg-horizontal-lines-background bg-contain pt-6 px-2 lg:px-12 h-[397px]'>
                <ScorecardSummaryBar
                  count={biomarkersCounter}
                  height={BASE_BAR_HEIGHT}
                  text={`Biomarker${biomarkersCounter !== 1 ? 's' : ''}`}
                  onClick={() => scrollIntoView()}
                />
              </div>
            ) : (
              <div className='flex flex-row gap-1 text-kelp items-end justify-center bg-horizontal-lines-background bg-contain pt-6 px-2 lg:px-12 h-[397px]'>
                <ScorecardSummaryBar
                  count={normalCount}
                  height={normalHeight}
                  text='Optimal / Normal'
                  onClick={() => scrollIntoView('Optimal / Normal')}
                />
                <ScorecardSummaryBar
                  count={outOfRangeCount}
                  height={outOfRangeHeight}
                  text='Out of range'
                  onClick={() => scrollIntoView('Out of range')}
                />
              </div>
            )}
          </>
        )}
      </div>
      {!isDesktopOrLargeTablet && (
        <div className='flex w-full flex-row items-center bg-kelp text-white rounded-lg px-6 py-4 overflow-auto'>
          {groupsNames.map((groupName, index) => (
            <div
              key={index}
              className={cn(
                'text-white opacity-60 hover:opacity-100 cursor-pointer',
                TextLayout.body2,
                {
                  'opacity-100': groupName === selectedGroup,
                }
              )}
              onClick={onSelectGroup(groupName)}
            >
              <div className={`w-max ${index === groupsNames.length - 1 ? 'pr-0' : 'pr-4'}`}>
                {groupName}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
